/* ------------------------------
   PAGE DEFAULTS
   Per-page color identity + background — fixed per page (no cycling).
   Applied on fresh load and re-applied after every AJAX page swap.
------------------------------ */

const PAGE_DEFAULTS = (window.GreenRoom && window.GreenRoom.pageDefaults) || {};


/* ------------------------------
   THEME DATA
   Six per-page color identities. ONE accent per page (--ui-accent):
   the single LIGHT color legible as text on the near-black ground.
   Single source of truth: window.GreenRoom.themes (theme.liquid).
------------------------------ */

const themes = (window.GreenRoom && window.GreenRoom.themes) || [];

let currentTheme = 0;


/* ------------------------------
   BACKGROUND DATA
   Objects come from window.GreenRoom.backgrounds (set in theme.liquid):
   { url, name (photographer), ig, pos (focal point), band, bandIg }
------------------------------ */

const backgrounds = (window.GreenRoom && window.GreenRoom.backgrounds) || [];
let currentBg = 0;


/* ------------------------------
   BACKGROUND SYSTEM
------------------------------ */

function setBackground(i) {
  if (!backgrounds.length) return;
  currentBg = ((i % backgrounds.length) + backgrounds.length) % backgrounds.length;
  const b = backgrounds[currentBg];

  const layer = document.getElementById('bg-layer');
  if (layer && b) {
    layer.style.backgroundImage    = `url(${b.url})`;
    layer.style.backgroundPosition = b.pos || '50% 50%';
  }

  renderPhotoCredit(b);
}

/* Band + photographer credit shown top-right over the hero / bottom-right over the footer.
   Cleared entirely when there's nothing to credit (e.g. the 404 background). */
function renderPhotoCredit(b) {
  const el = document.querySelector('.photo-credit');
  if (!el) return;
  if (!b || (!b.name && !b.band)) { el.innerHTML = ''; return; }
  const cred = function(handle, lbl, text) {
    return handle
      ? '<a href="https://instagram.com/' + handle + '" target="_blank" rel="noopener"><span class="by">' + lbl + '</span> ' + text + '</a>'
      : '<span><span class="by">' + lbl + '</span> ' + text + '</span>';
  };
  const parts = [];
  if (b.band) parts.push(cred(b.bandIg, 'band', b.band.toUpperCase()) + '<span class="sep">·</span>');
  if (b.name) parts.push(cred(b.ig, 'photo', b.name.toUpperCase()));
  el.innerHTML = parts.join('');
}

function preloadBackgrounds() {
  // The current + next photo load now (instant first cycle); the rest wait for idle
  // so a first visit doesn't pay for every background up front.
  const later = [];
  backgrounds.forEach(function(b, i) {
    if (i === currentBg || i === (currentBg + 1) % backgrounds.length) {
      new Image().src = b.url;
    } else {
      later.push(b.url);
    }
  });
  const loadRest = function() { later.forEach(function(url) { new Image().src = url; }); };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadRest, { timeout: 4000 });
  } else {
    setTimeout(loadRest, 2500);
  }
}


/* ------------------------------
   THEME SYSTEM
------------------------------ */

function setTheme(i) {
  if (!themes.length) return;
  currentTheme = ((i % themes.length) + themes.length) % themes.length;
  const t = themes[currentTheme];
  const d = document.documentElement.style;

  // Dual var names so both the ported prototype CSS and legacy selectors resolve.
  d.setProperty('--ui-text',     t.text);       d.setProperty('--text',        t.text);
  d.setProperty('--ui-accent',   t.uiAccent);
  d.setProperty('--bar-color',   t.accent);     d.setProperty('--accent',      t.accent);
  d.setProperty('--bar-text',    t.accentText); d.setProperty('--accent-text', t.accentText);
  d.setProperty('--nav-btn',     t.navBtn);
  d.setProperty('--page-bg',     t.pageBg);
  d.setProperty('--tint',        t.tint);
  d.setProperty('--surface',     '#101820');
  d.setProperty('--surface-border', 'rgba(255,255,255,0.14)');
  d.setProperty('--text-muted',  'rgba(240,238,233,0.7)');

  document.documentElement.dataset.theme = t.name;
}


/* ------------------------------
   POPUP SYSTEM
------------------------------ */

// Used by nav buttons — toggles the popup closed if already open
function navPopup(id) {
  const popup = document.getElementById(id);
  if (!popup) return;
  const isOpen = !popup.classList.contains('hidden');
  document.querySelectorAll('.side-popup').forEach(p => p.classList.add('hidden'));
  if (!isOpen) {
    popup.classList.remove('hidden');
    // Hand focus to the popup so keyboard / screen-reader users land inside it.
    // (No focus trap on purpose — the bottom bar stays interactive while a popup is open.)
    const closeBtn = popup.querySelector('.popup-close');
    if (closeBtn) closeBtn.focus();
  }
}

function closeAllPopups() {
  let closed = false;
  document.querySelectorAll('.side-popup:not(.hidden), .flyer-popup:not(.hidden)').forEach(p => {
    p.classList.add('hidden');
    closed = true;
  });
  return closed;
}


/* ------------------------------
   AJAX NAVIGATION
   Swaps only <main> on internal links so the
   Spotify iframe stays alive across page changes
------------------------------ */

function setAjaxNavProgress(on) {
  const el = document.getElementById('ajax-nav-progress');
  if (!el) return;
  el.classList.toggle('ajax-nav-progress--active', !!on);
  el.setAttribute('aria-hidden', on ? 'false' : 'true');
}

async function ajaxNavigate(path, push) {
  if (push === undefined) push = true;
  setAjaxNavProgress(true);
  try {
    const res = await fetch(path);
    // Shopify's 404 response is a full themed page (#page-content + 404 template),
    // so swap it in like any other page — a hard redirect would kill the radio.
    if (!res.ok && res.status !== 404) { location.href = path; return; }

    const html    = await res.text();
    const doc     = new DOMParser().parseFromString(html, 'text/html');
    const newMain = doc.getElementById('page-content');
    const curMain = document.getElementById('page-content');
    if (!newMain || !curMain) { location.href = path; return; }

    curMain.innerHTML = newMain.innerHTML;

    // Re-execute the incoming page's JS (inline + external), keeping every attribute.
    // Non-JS scripts (application/ld+json) are left as parsed — running them would throw.
    curMain.querySelectorAll('script').forEach(old => {
      const type = (old.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module') return;
      const s = document.createElement('script');
      for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
      s.textContent = old.textContent;
      old.replaceWith(s);
    });

    document.title = doc.title;
    // popstate navigations reuse the existing history entry — pushing again would bury it.
    if (push) history.pushState({ path }, '', path);
    // Track 404 state per hop so the 404 background + theme apply (and never stick to the next page).
    if (window.GreenRoom) window.GreenRoom.is404 = (res.status === 404);
    applyPageDefaults();
    setActiveNav();

    // Re-run the header animation + scroll reveal on the freshly-swapped content.
    if (window.GreenRoomChrome) window.GreenRoomChrome.init();
    window.scrollTo(0, 0);
    // Hand focus to the new content so assistive tech knows the page changed.
    curMain.setAttribute('tabindex', '-1');
    curMain.focus({ preventScroll: true });
  } catch (_) {
    location.href = path;
  } finally {
    setAjaxNavProgress(false);
  }
}

function initAjaxNav() {
  document.addEventListener('click', e => {
    const a = e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    try {
      const url = new URL(a.href, location.origin);
      if (url.origin !== location.origin) return;
      // Checkout + account live outside the theme (and may redirect cross-domain) — full loads only.
      if (/^\/(checkout|cart\/c\/|account|admin)/.test(url.pathname)) return;
      e.preventDefault();
      ajaxNavigate(url.pathname + url.search);
    } catch (_) {}
  });

  window.addEventListener('popstate', () => {
    ajaxNavigate(location.pathname + location.search, false);
  });
}


/* ------------------------------
   ACTIVE NAV
------------------------------ */

function setActiveNav() {
  const path = window.location.pathname;
  document.querySelectorAll('#right-nav a.nav-btn').forEach(a => {
    a.classList.toggle('active', a.getAttribute('href') === path);
  });
}


/* ------------------------------
   PAGE DEFAULTS
------------------------------ */

function applyPageDefaults() {
  if (window.GreenRoom && window.GreenRoom.is404) {
    setTheme(4); // Notice (cream)
    const layer = document.getElementById('bg-layer');
    if (layer && window.GreenRoom.bg404) {
      layer.style.backgroundImage = `url(${window.GreenRoom.bg404})`;
      layer.style.backgroundPosition = '50% 50%';
    }
    renderPhotoCredit(null); // no credit on the 404 background
    return;
  }
  const path = window.location.pathname;
  const defaults = PAGE_DEFAULTS[path]
    || (path.startsWith('/products/') ? { theme: 5, bg: 2 } : null)
    || { theme: 0, bg: 0 };
  setBackground(defaults.bg);
  setTheme(defaults.theme);
}


/* ------------------------------
   PHONE NORMALIZATION
------------------------------ */

function normalizePhone(raw) {
  var digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits[0] === '1') return '+' + digits;
  return raw.startsWith('+') ? raw : '+' + digits;
}


/* ------------------------------
   AJAX FORM HANDLING
   Submits forms via fetch so the Spotify player survives
------------------------------ */

function initAjaxForms() {
  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    // Never intercept checkout
    if (form.querySelector('[name="checkout"]') === document.activeElement) return;
    if (form.hasAttribute('data-no-ajax')) return;

    var action = form.getAttribute('action') || '';

    // Cart update form
    if (action.startsWith('/cart') && !action.startsWith('/cart/')) {
      e.preventDefault();
      var formData = new FormData(form);
      fetch(action, {
        method: form.method || 'POST',
        body: formData
      })
      .then(function() {
        refreshCart();
        // Re-render the cart page itself so its line items + subtotal match.
        if (window.location.pathname.indexOf('/cart') === 0) {
          ajaxNavigate('/cart', false);
        }
      })
      .catch(function() { form.submit(); });
      return;
    }

    // Newsletter subscribe — post to Klaviyo Client API
    if (form.id === 'subscribe-form') {
      e.preventDefault();

      var email = form.querySelector('[name="email"]').value.trim();
      var phone = form.querySelector('[name="phone"]').value.trim();
      var smsConsent = form.querySelector('[name="sms_consent"]');
      var feedback = document.getElementById('contact-feedback');

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (feedback) {
          feedback.className = 'contact-feedback contact-feedback--error';
          feedback.textContent = 'Please enter a valid email address.';
          feedback.style.display = 'block';
        }
        return;
      }

      var profileAttrs = { email: email };

      if (phone) {
        profileAttrs.phone_number = normalizePhone(phone);
      }

      var apiUrl = 'https://a.klaviyo.com/client/subscriptions/?company_id=XyJ6dz';
      var apiHeaders = { 'Content-Type': 'application/json', 'revision': '2026-01-15' };
      var wantsSms = phone && smsConsent && smsConsent.checked;

      function buildPayload(listId, attrs) {
        return JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Green Room Website',
              profile: { data: { type: 'profile', attributes: attrs } }
            },
            relationships: {
              list: { data: { type: 'list', id: listId } }
            }
          }
        });
      }

      // Always subscribe to the email list
      var emailAttrs = Object.assign({}, profileAttrs);
      emailAttrs.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };

      var requests = [
        fetch(apiUrl, { method: 'POST', headers: apiHeaders, body: buildPayload('UjYj3b', emailAttrs) })
      ];

      // If SMS opted in, also subscribe to the text list
      if (wantsSms) {
        var smsAttrs = Object.assign({}, profileAttrs);
        smsAttrs.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
        requests.push(
          fetch(apiUrl, { method: 'POST', headers: apiHeaders, body: buildPayload('Vc8Ugg', smsAttrs) })
        );
      }

      Promise.all(requests)
      .then(function(results) {
        var allOk = results.every(function(r) { return r.ok || r.status === 202; });
        if (!allOk) {
          results.forEach(function(r, i) {
            if (!r.ok && r.status !== 202) r.text().then(function(t) { console.error('Klaviyo request ' + i + ' failed (' + r.status + '):', t); });
          });
        }
        if (feedback) {
          if (allOk) {
            feedback.className = 'contact-feedback contact-feedback--success';
            feedback.textContent = wantsSms
              ? "You're on the list. Check your phone to confirm texts."
              : "You're on the list.";
          } else {
            feedback.className = 'contact-feedback contact-feedback--error';
            feedback.textContent = 'Something went wrong. Try again.';
          }
          feedback.style.display = 'block';
          setTimeout(function() { feedback.style.display = 'none'; }, 5000);
        }
        if (allOk) form.reset();
      })
      .catch(function() {
        if (feedback) {
          feedback.className = 'contact-feedback contact-feedback--error';
          feedback.textContent = 'Network error. Please try again.';
          feedback.style.display = 'block';
          setTimeout(function() { feedback.style.display = 'none'; }, 5000);
        }
      });

      return;
    }

    // Legacy contact POST (non-Storefront)
    if (action.indexOf('/contact') !== -1) {
      e.preventDefault();
      var contactData = new FormData(form);
      fetch(action, {
        method: 'POST',
        body: contactData
      })
      .then(function(res) {
        var fb = document.getElementById('contact-feedback');
        if (fb) {
          if (res.ok) {
            fb.className = 'contact-feedback contact-feedback--success';
            fb.textContent = 'Sent!';
          } else {
            fb.className = 'contact-feedback contact-feedback--error';
            fb.textContent = 'Something went wrong. Try again.';
          }
          fb.style.display = 'block';
          setTimeout(function() { fb.style.display = 'none'; }, 5000);
        }
        form.reset();
      })
      .catch(function() { form.submit(); });
      return;
    }
  });
}


/* ------------------------------
   INIT
------------------------------ */

document.addEventListener('DOMContentLoaded', () => {
  applyPageDefaults();
  preloadBackgrounds();
  setActiveNav();
  initAjaxNav();
  initAjaxForms();

  // Show/hide SMS consent checkbox when phone has a value
  var phoneInput = document.querySelector('#subscribe-form [name="phone"]');
  var smsRow = document.getElementById('sms-consent-row');
  if (phoneInput && smsRow) {
    phoneInput.addEventListener('input', function() {
      smsRow.style.display = this.value.trim() ? 'flex' : 'none';
      if (!this.value.trim()) {
        var cb = smsRow.querySelector('input[type="checkbox"]');
        if (cb) cb.checked = false;
      }
    });
  }

  document.querySelectorAll('.popup-close').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.closest('.side-popup')?.classList.add('hidden');
    });
  });

  // Esc closes any open popup
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && closeAllPopups()) e.preventDefault();
  });
});
